// Backend
import React, { useCallback } from 'react';
import axios from '../../../helpers/AxiosInstance';

// Frontend
import { Grid, Cell } from 'react-foundation';
import { PlusCircle } from 'react-bootstrap-icons';
import { Modal } from 'react-bootstrap';
import '../../../stylesheets/components/Categories.css';

// Functionality
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

// Components
import {
  Header,
  DluchApi,
  FlashMessage,
  Flashable,
  ProgressBar,
  disableFormButton,
  enableFormButton,
  ErrorRender,
  setToken,
  filteredCategories,
  filterCategoriesAgainstType
} from '../../../constants/SharedComponents';

import useCurrentUser from '../../../hooks/useCurrentUser';
import QuestionCategory from '../../../components/QuestionCategory';
import useInternalBlocker from '../../../hooks/useInternalBlocker';
import PropType from 'prop-types';

export function ConversationsCategoriesEdit() {
  // Params
  const { conversationId } = useParams();
  const navigate = useNavigate();

  // Objects
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [inputs, setInputs] = React.useState({});

  // API
  const currentUser = useCurrentUser();
  const { state } = useLocation();
  const token = setToken(currentUser);
  const headerParams = { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 };
  const [loading, setLoading] = React.useState(true);
  const [questionResultData, setQuestionResultData] = React.useState(null);
  const [questionCategoriesData, setQuestionCategoriesData] = React.useState(null);
  const [deselectedCategories, setDeselectedCategories] = React.useState([]);
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [currentModal, setCurrentModal] = React.useState(null);
  const [submissionUnlocked, setSubmissionUnlocked] = React.useState(false);
  const [guidedRoute, setGuidedRoute] = React.useState(null);
  const [formChanged, setFormChanged] = React.useState(false);
  const [hasUserSaved, setHasUserSaved] = React.useState(false);
  const [hasExistingData, setHasExistingData] = React.useState(false);
  const [questionScores, setQuestionScores] = React.useState(null);
  const [isFinished, setIsFinished] = React.useState(false);

  const basicFormRef = React.useRef(null);
  const basicFormButtons = React.useRef([]);
  const categoryFormButtons = React.useRef([]);

  const handleGoBack = useCallback(() => {
    // Check if preparation is completed OR if user has saved changes OR if user has existing data (returning to finish)
    if (data?.employeePrepStatus === 2 || hasUserSaved || hasExistingData) {
      navigate('/conversations');
    } else {
      navigate(`/conversations/${conversationId}/team-member-prep/routes`);
    }
  }, [navigate, conversationId, data, hasUserSaved, hasExistingData]);

  // Functions
  function openModal(event) {
    event.preventDefault();
    setCurrentModal(event.target.getAttribute('data-modal-id'));

  }

  function closeModal() {
    setCurrentModal(null);
  }

  function processFormData(formInputs) {
    let categoryIds = [];
    let index = 0;
    let questionResults = [];

    for (const key of Object.keys(formInputs)) {
      let matches = key.split('--');

      if (matches[1] === undefined || matches[1] === 'null') {
        continue;
      }

      categoryIds.push(matches[1]);
    }

    categoryIds = Array.from(new Set(categoryIds));

    for (const categoryId in categoryIds) {
      const key = 'notes--' + categoryIds[categoryId];

      let notes = '';

      if (formInputs.hasOwnProperty(key)) {
        notes = formInputs[key];
      }

      let formData = {};

      formData['notes'] = notes;
      formData['isConversationTopic'] =
        formInputs['isConversationTopic--' + categoryIds[categoryId]] !== undefined
          ? formInputs['isConversationTopic--' + categoryIds[categoryId]]
          : false;
      formData['conversationId'] = conversationId;
      formData['categoryId'] = categoryIds[categoryId];
      formData['priorityRank'] = formInputs['priorityRank--' + categoryIds[categoryId]];
      formData['optionalCategoryName'] =
        formInputs['optionalCategoryName--' + categoryIds[categoryId]];
      questionResults[index] = formData;
      index++;
    }

    return questionResults;
  }

  const sortedQuestionResultData = function (categoryData) {
    let responseData = categoryData.sort(function (a, b) {
      let weight = -1;

      if (a.priorityRank > b.priorityRank) {
        weight = 1;
      } else if (a.priorityRank === b.priorityRank) {
        weight = a.categoryName > b.categoryName ? 1 : -1;
      }

      return weight;
    });

    responseData = responseData.reverse();

    responseData = responseData.sort(function (a, b) {
      return a.categoryType - b.categoryType;
    });

    return responseData;
  };

  function createSubmission(formInputs, complete) {
    let questionResults = processFormData(formInputs);
    let Api = new DluchApi('QuestionResultsEdit');
    let requestPayload;

    // For loop though selectedCategories, add the questionResult with the same category to a new questionResults array (modify the priorityRank)
    // Do the same after for deselected categories
    // This should get the exact order of the categories

    let newQuestionResults = []
    let newPriorityRank = 1;

    let chosenCategories = selectedCategories.map(obj => ({ ...obj }));
    let notChosenCategories = fixNotSelectedCategoryList(chosenCategories);

    for (const category of chosenCategories) {
      for (const result of questionResults) {
        if (result.categoryId === category.id) {
          let newResult = result;
          newResult.priorityRank = newPriorityRank;
          newQuestionResults.push(result);
          newPriorityRank++;
          break;
        }
      }
    }

    for (const category of notChosenCategories) {
      for (const result of questionResults) {
        if (result.categoryId === category.id) {
          let newResult = result;
          newResult.priorityRank = newPriorityRank;
          newQuestionResults.push(result);
          newPriorityRank++;
          break;
        }
      }
    }

    // Do something here
    requestPayload = { questionResults: newQuestionResults, generalNotes: formInputs.general_notes, isFinished: isFinished };

    axios
      .put(Api.ApiRequestUrl(), requestPayload, headerParams)
      .then(() => {
        setFormChanged(false);
        setHasUserSaved(true);

        if (!complete) {
          setFlashMessageObject(Flashable('Changes were successfully saved.', 'notice', false));
        }
      })
      .catch((error) => {
        if (error.response?.data) {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
        } else {
          setFlashMessageObject(Flashable(error.message, 'alert', false));
        }
      });

    enableFormButton(basicFormButtons);
    setLoading(false);
  }

  const fixNotSelectedCategoryList = function (chosenCategories) {
    let notChosenCategories = deselectedCategories.map(obj => ({ ...obj }));

    for (const category of chosenCategories) {
      for (const nCategory of notChosenCategories) {
        if (category.id === nCategory.id) {
          let index = notChosenCategories.indexOf(nCategory);
          notChosenCategories.splice(index, 1);
        }
      }
    }

    return notChosenCategories;
  }

  function createQuestionResults(inputs, complete) {
    setLoading(true);
    setFormChanged(false);
    setHasUserSaved(true);

    let Api = new DluchApi('QuestionsNew');
    let questionScoresData = JSON.parse(localStorage.getItem('questionScores'));
    let requestPayload = {
      questionScores: questionScoresData['questionScores'].flat(),
    };

    axios
      .post(Api.ApiRequestUrl(), requestPayload, headerParams)
      .then(() => {
        localStorage.removeItem('questionScores');
        createSubmission(inputs, complete);
      })
      .catch((error) => {
        if (error.response?.data) {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
        } else {
          setFlashMessageObject(Flashable(error.message, 'alert', false));
        }
        setLoading(false);
      });
  }

  const getQuestionScores = async () => {
    setLoading(true);

    try {
      let Api = new DluchApi('QuestionScoresShow', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.sortedScores;
      setError(false);
      getTemperatureQuestionCategory(responseData[0]);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  async function getGuidedRoute() {
    try {
      let Api = new DluchApi('QuestionScoresShow', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.sortedScores;

      if (responseData.length > 1) {
        setGuidedRoute(true);
        return true;
      }
      else {
        setGuidedRoute(false);
        return false;
      }

    } catch {
      setGuidedRoute(false);
      return false;
    }
  }

  const getTemperatureQuestionCategory = async (questionScores) => {
    setLoading(true);

    try {
      let Api = new DluchApi('QuestionsCategoriesIndex', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.categories;
      let result = filteredCategories(responseData, 3);
      getQuestionOptions(questionScores, result[0]);
      setError(false);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionOptions = async (questionScores, temperatureQuestionCategory) => {
    setLoading(true);

    try {
      let Api = new DluchApi('QuestionsIndex', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content;
      let result = responseData.sortedQuestions.find(
        (obj) => obj.categoryId === temperatureQuestionCategory.id,
      );
      questionScores.optionText = JSON.parse(result.questionOptions).find(
        (obj) => obj.OptionScore === questionScores.score,
      ).OptionText;
      setQuestionScores(questionScores);
      setError(false);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getData = async () => {
    setLoading(true);

    try {
      let Api = new DluchApi('ConversationsShow', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.conversation;
      const conversationSubmissionUnlocked = !(responseData.isCanvasData);

      setData(responseData);

      setSubmissionUnlocked(conversationSubmissionUnlocked);
      getQuestionCategoriesData(conversationSubmissionUnlocked);
      setError(false);

      if (!conversationSubmissionUnlocked) {
        getQuestionScores();
      }
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionCategoriesData = async (conversationSubmissionUnlocked) => {
    setLoading(true);

    try {
      let Api = new DluchApi('QuestionsCategoriesIndex', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.categories;
      let allCategories = filteredCategories(responseData, 3, false);

      const isGuidedRoute = await getGuidedRoute();

      if (isGuidedRoute === true) {
        allCategories = filterCategoriesAgainstType(allCategories, 2);
      }
      else {
        allCategories = filterCategoriesAgainstType(allCategories, 1);
      }

      setError(false);
      getQuestionResultData(allCategories, conversationSubmissionUnlocked);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionResultData = async (questionCategories, conversationSubmissionUnlocked) => {
    setLoading(true);

    try {
      let Api = new DluchApi('QuestionResultsShow', conversationId);
      let defaultInputs = {};
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);

      setIsFinished(response.data.content.isFinished);

      defaultInputs['general_notes'] = response.data.content.generalNotes;

      let responseData = sortedQuestionResultData(response.data.content.results);

      let chosenCategories = responseData.filter((obj) => obj.isConversationTopic === true);

      let notChosenCategories = responseData;

      chosenCategories = chosenCategories.map((obj) => obj.categoryId);
      notChosenCategories = notChosenCategories.map((obj) => obj.categoryId);
      chosenCategories = questionCategories.filter((obj) => chosenCategories.includes(obj.id));
      notChosenCategories = questionCategories.filter((obj) =>
        notChosenCategories.includes(obj.id),
      );

      for (const conversationTopic of responseData) {
        defaultInputs['isConversationTopic--' + conversationTopic.categoryId] =
          conversationTopic.isConversationTopic;
        defaultInputs['priorityRank--' + conversationTopic.categoryId] =
          conversationTopic.priorityRank;
        defaultInputs['notes--' + conversationTopic.categoryId] = conversationTopic.notes;
        defaultInputs['optionalCategoryName--' + conversationTopic.categoryId] =
          conversationTopic.optionalCategoryName;
      }


      let guidedOptionalCategories = questionCategories.filter((obj) => obj.categoryType === 1);
      guidedOptionalCategories = guidedOptionalCategories.map((obj) => obj.Id);

      let selfGuidedCategories = questionCategories.filter((obj) => obj.categoryType === 2);
      selfGuidedCategories = selfGuidedCategories.map((obj) => obj.Id);

      for (let row of questionCategories) {
        row.selfGuided =
          guidedOptionalCategories.includes(row.id) || selfGuidedCategories.includes(row.id);
      }

      if (!conversationSubmissionUnlocked) {
        for (let row of questionCategories) {
          row.delete = row.selfGuided && !row.optionalCategoryName;
        }

        for (let row of notChosenCategories) {
          row.delete = row.selfGuided && !row.optionalCategoryName;
        }

        questionCategories = questionCategories.filter((obj) => obj.delete !== true);
        notChosenCategories = notChosenCategories.filter((obj) => obj.delete !== true);
      }

      let sortedChosenResults = sortChosenResults(chosenCategories, responseData);
      let sortedNotChosenResults = sortNotChosenResults(notChosenCategories, responseData);

      sortedChosenResults.sort(function (a, b) { return a.priorityRank - b.priorityRank });
      sortedNotChosenResults.sort(function (a, b) { return a.priorityRank - b.priorityRank });

      let sortedChosenCategories = sortChosenCategories(sortedChosenResults, chosenCategories);
      let sortedNotChosenCategories = sortNotChosenCategories(sortedNotChosenResults, notChosenCategories);


      let superSortedNotChosenCategories = sortedNotChosenCategories.slice().sort((a, b) => {
        const isSelfA = a.name.includes("Self-Guided Optional");
        const isSelfB = b.name.includes("Self-Guided Optional");

        if (isSelfA && !isSelfB) return 1;
        if (!isSelfA && isSelfB) return -1;

        return a.name.localeCompare(b.name);
      });

      superSortedNotChosenCategories = superSortedNotChosenCategories.slice().sort((a, b) => {
        const isSelfA = a.name.includes("Guided Optional");
        const isSelfB = b.name.includes("Guided Optional");

        if (isSelfA && !isSelfB) return 1;
        if (!isSelfA && isSelfB) return -1;

        return a.name.localeCompare(b.name);
      });

      const sortedChosenIds = new Set(sortedChosenCategories.map(c => c.id));
      superSortedNotChosenCategories = superSortedNotChosenCategories.filter(c => !sortedChosenIds.has(c.id));

      setQuestionCategoriesData(questionCategories);
      setSelectedCategories(sortedChosenCategories);
      setDeselectedCategories(superSortedNotChosenCategories);
      setInputs(defaultInputs);
      setQuestionResultData(responseData);

      // Check if user has existing data (returning to finish prep)
      if (responseData && responseData.length > 0) {
        setHasExistingData(true);
      }

      setError(false);
    } catch {
      setSelectedCategories([]);

      let defaultInputs = {};

      let selfGuidedCategories = questionCategories.filter((obj) => obj.categoryType === 2);
      selfGuidedCategories = selfGuidedCategories.map((obj) => obj.id);

      let guidedOptionalCategories = questionCategories.filter((obj) => obj.categoryType === 1);
      guidedOptionalCategories = guidedOptionalCategories.map((obj) => obj.id);

      for (let row of questionCategories) {
        row.selfGuided = selfGuidedCategories.includes(row.id);
        row.delete = guidedOptionalCategories.includes(row.id);
      }

      const filteredQuestionCategories = questionCategories.filter((obj) => obj.delete !== true);

      for (let i = 0; i < filteredQuestionCategories.length; ++i) {
        const category = filteredQuestionCategories[i];
        defaultInputs['isConversationTopic--' + category.id] =
          false;
        defaultInputs['priorityRank--' + category.id] =
          i;
        defaultInputs['notes--' + category.id] = "";
        defaultInputs['optionalCategoryName--' + category.id] =
          null;
      }

      setQuestionCategoriesData(filteredQuestionCategories);
      setDeselectedCategories(filteredQuestionCategories);
      setInputs(defaultInputs);
    } finally {
      setLoading(false);
    }
  };

  const sortChosenResults = function (chosenCategories, responseData) {
    let sortedChosenResults = [];

    for (const category of chosenCategories) {
      for (const result of responseData) {
        if (result.categoryId === category.id) {
          sortedChosenResults.push(result);
          break;
        }
      }
    }

    return sortedChosenResults;
  }

  const sortNotChosenResults = function (notChosenCategories, responseData) {
    let sortedNotChosenResults = [];

    for (const category of notChosenCategories) {
      for (const result of responseData) {
        if (result.categoryId === category.id) {
          sortedNotChosenResults.push(result);
          break;
        }
      }
    }

    return sortedNotChosenResults;
  }

  const sortChosenCategories = function (sortedChosenResults, chosenCategories) {
    let sortedChosenCategories = [];

    for (const result of sortedChosenResults) {
      for (const category of chosenCategories) {
        if (category.id === result.categoryId) {
          sortedChosenCategories.push(category);
          break;
        }
      }
    }

    return sortedChosenCategories;
  }

  const sortNotChosenCategories = function (sortedNotChosenResults, notChosenCategories) {
    let sortedNotChosenCategories = [];

    for (const result of sortedNotChosenResults) {
      for (const category of notChosenCategories) {
        if (category.id === result.categoryId) {
          sortedNotChosenCategories.push(category);
          break;
        }
      }
    }

    return sortedNotChosenCategories;
  }

  function retryFetch() {
    setLoading(true);
    setError(false);
    setData(null);
    getData();
  }

  // @see https://css-tricks.com/run-useeffect-only-once/#
  // The second param will ensure the useEffect only runs once.
  // eslint-disable-next-line
  React.useEffect(() => {
    if (currentUser?.token) {
      retryFetch();
    }

    if (localStorage.getItem('questionScores') !== null) {
      setFormChanged(true);
    }

    if (state?.hasOwnProperty('flash')) {
      setFlashMessageObject(state.flash);
    }
  }, [currentUser, state]);

  // Call handleCheckboxes when the modal opens
  React.useEffect(() => {
    if (currentModal === 'category-private-notes') {
      handleCheckboxes();
    }
  }, [currentModal]);

  const InternalNavigationChecker = ({ when }) => {
    useInternalBlocker((tx) => {
      if (submissionUnlocked && formChanged) {
        handleSave();
      }
      tx.retry();
    }, when);

    return null;
  };

  InternalNavigationChecker.propTypes = {
    when: PropType.any,
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    disableFormButton(basicFormButtons);
    let complete = event.target.getAttribute('data-complete') === 'true';

    if (localStorage.getItem('questionScores') !== null) {
      createQuestionResults(inputs, complete);
    } else {
      createSubmission(inputs, complete);
    }
  };

  const handleSave = () => {
    if (submissionUnlocked) {
      setLoading(true);
      disableFormButton(basicFormButtons);
      let complete = false;

      if (localStorage.getItem('questionScores') !== null) {
        createQuestionResults(inputs, complete);
      } else {
        createSubmission(inputs, complete);
      }
    }
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
    setFormChanged(true);
  };

  const selectCategory = (event) => {
    event.preventDefault();
    setFormChanged(true);
    setLoading(true);

    let category = deselectedCategories.find((obj) => {
      return obj.id === event.target.getAttribute('data-category-id');
    });

    if (category) {
      let formInputs = inputs;
      let selectCategories = selectedCategories;

      formInputs['isConversationTopic--' + category.id] = true;
      setInputs(formInputs);

      selectCategories.push(category);
      setSelectedCategories(sortedQuestionResultData(selectCategories));
    }

    setLoading(false);

    handleSave();
  };

  const selectCategories = (event) => {
    event.preventDefault();
    setFormChanged(true);
    setLoading(true);

    let selectedCategoriesCopy = selectedCategories.slice();

    for (const category of selectedCategoriesCopy) {
      if (category) {
        let formInputs = inputs;

        formInputs['isConversationTopic--' + category.id] = false;
        setInputs(formInputs);
      }
    }

    setSelectedCategories([]);

    let allCategories = Array.from(document.querySelectorAll('input[type="checkbox"]:checked'))
      .map(checkbox => {
        const categoryId = checkbox.getAttribute('data-category-id');
        return deselectedCategories.find(obj => obj.id === categoryId) ||
          selectedCategories.find(obj => obj.id === categoryId);
      });

    let categories = allCategories.slice(0, 3);

    let selectCategories = [];
    let formInputs = inputs;

    for (const category of categories) {

      if (category) {
        formInputs['isConversationTopic--' + category.id] = true;
        selectCategories.push(category);
      }
    }

    let allDeselectedCategories = Array.from(document.querySelectorAll('input[type="checkbox"]:not(:checked)'))
      .map(checkbox => {
        const categoryId = checkbox.getAttribute('data-category-id');
        return deselectedCategories.find(obj => obj.id === categoryId) ||
          selectedCategories.find(obj => obj.id === categoryId);
      });

    let deselectCategories = [];

    for (const category of allDeselectedCategories) {
      if (category) {
        deselectCategories.push(category);
      }
    }

    setSelectedCategories(sortedQuestionResultData(selectCategories));
    setDeselectedCategories(sortedQuestionResultData(deselectCategories));
    setInputs(formInputs);

    setLoading(false);
    closeModal();
    handleSave();
  };

  const handleCheckboxes = () => {
    for (const category of selectedCategories) {
      let categoryCheckbox = (Array.from(document.querySelectorAll('input[type="checkbox"]'))).find(i => i.getAttribute("data-category-id") === category.id);

      if (categoryCheckbox) {
        categoryCheckbox.checked = true;
      }
    }
  }

  const deselectCategory = (event) => {
    event.preventDefault();
    setFormChanged(true);
    setLoading(true);

    let category = selectedCategories.find((obj) => {
      return obj.id === event.target.parentNode.getAttribute('data-category-id');
    });

    if (category) {
      let formInputs = inputs;

      formInputs['isConversationTopic--' + category.id] = false;
      setInputs(formInputs);

      let selectCategories = selectedCategories.filter((obj) => {
        return obj.id !== event.target.parentNode.getAttribute('data-category-id');
      });
      
      let deselectCategories = [...deselectedCategories, category];

      setSelectedCategories(sortedQuestionResultData(selectCategories));
      setDeselectedCategories(sortedQuestionResultData(deselectCategories));
    }

    setLoading(false);

    handleSave();
  };

  const questionResultsContent = (questionCategoryId, field) => {
    if (inputs[field + '--' + questionCategoryId]) {
      return inputs[field + '--' + questionCategoryId];
    }

    if (!questionResultData) {
      return;
    }

    const category = questionResultData.find((obj) => {
      return obj.categoryId === questionCategoryId;
    });

    if (category) {
      return category[field];
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: '1 0 auto' }}>
        <ProgressBar loading={loading} />
        <Header />
        <FlashMessage object={flashMessageObject} columnWrap={true} />
        {error && (
          <ErrorRender object={error} />
        )}

        {data && questionCategoriesData ? (
          <div className="grid-container">
            <Grid className="grid-padding-x">
              <Cell small={12}>
                {data?.isUserManager && (
                  <div className="page-content">
                    <h1 className="page-content__title">Manager View</h1>
                    <p>This is the manager view for conversation categories.</p>
                  </div>
                )}
                {!data?.isUserManager && (
                  <>
                    <form onSubmit={handleSubmit}>
                      {submissionUnlocked && guidedRoute === true && (
                        <>
                          <div className="page-notice">
                            <h1 className="page-notice__title">Survey complete</h1>
                            <div className="page-notice__content">
                              <p>
                                Based on your scores, we have preselected three suggested topics for your
                                conversation. These are the three areas in which your score suggested most
                                prevalent risks to your wellbeing. You can read more about how the results
                                are calculated in our{' '}
                                <Link to="/resources" target="_blank" rel="noopener noreferrer">
                                  resources area
                                </Link>
                                .
                              </p>
                            </div>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <button
                              className="page-content__button--medium page-content__button--back mb-3"
                              id="page-navigation-button--go-back"
                              onClick={handleGoBack}
                            >
                              Go back
                            </button>

                            <button
                              className="page-content__button--medium mb-3"
                              id="page-navigation-button--save-changes"
                              onClick={handleSave}
                              disabled={!formChanged}
                              style={{ position: 'absolute', right: '0', bottom: '0' }}
                            >
                              Save Changes
                            </button>
                          </div>

                          <div className="page-content">
                            <h3 className="page-content__subtitle page-content__subtitle--section">
                              What you need to do
                            </h3>
                          </div>
                        </>
                      )}

                      {submissionUnlocked && guidedRoute === false && (
                        <>
                          <div className="page-notice">
                            <h1 className="page-notice__title">Self-reflection route</h1>
                            <div className="page-notice__content">
                              <p>
                                Please consider up to the three characteristics you would like to take to
                                your meeting. You can choose from the 7 characteristics listed or include
                                specific issues of your own.
                              </p>

                              <p>
                                It is really helpful to think about what specific aspects of each work
                                characteristic is important to discuss with your manager and also what are
                                the effects of this on you.
                              </p>
                            </div>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <button
                              className="page-content__button--medium page-content__button--back mb-3"
                              id="page-navigation-button--go-back"
                              onClick={handleGoBack}
                            >
                              Go back
                            </button>

                            <button
                              className="page-content__button--medium mb-3"
                              id="page-navigation-button--save-changes"
                              onClick={handleSave}
                              disabled={!formChanged}
                              style={{ position: 'absolute', right: '0', bottom: '0' }}
                            >
                              Save Changes
                            </button>
                          </div>

                          <div className="page-content__styles">
                            <h2 className="page-content__subtitle page-content__subtitle--section">
                              What you need to do
                            </h2>

                            <p>
                              Choose up to three conversation topics.
                            </p>
                          </div>
                        </>
                      )}

                      {!submissionUnlocked && (
                        <div className="page-content">
                          <button
                            className="page-content__button--medium page-content__button--back mb-3"
                            id="page-navigation-button--go-back"
                            onClick={handleGoBack}
                          >
                            Go back
                          </button>

                          <h1 id="selected-categories" className="page-content__title">
                            Team Member Prep - Submitted
                          </h1>
                        </div>
                      )}

                      {!submissionUnlocked && (
                        <div className="grid-container">
                          <Grid className="grid-padding-x">
                            <Cell small={12}>
                              <div className="page-content">
                                <div className="page-content__styles">
                                  <p>
                                    Your prep information has been submitted and can no longer be
                                    amended.
                                  </p>

                                  {questionScores && (
                                    <>
                                      <h3 className="page-content__subtitle">
                                        {questionScores.questionText}
                                      </h3>

                                      <div className="page-content__styles">
                                        <p>
                                          <b>{questionScores.optionText}</b>
                                        </p>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </Cell>
                          </Grid>
                        </div>
                      )}
                    </form>

                    {(submissionUnlocked || selectedCategories.length > 0 || (!submissionUnlocked && inputs.general_notes)) && (
                      <form
                        className="basic-form"
                        ref={basicFormRef}
                        id="selected-categories"
                      >
                        <InternalNavigationChecker when={true} />
                        <div className="question-categories-choice">
                          {submissionUnlocked && (
                            <>
                              <h2 className="question-categories-choice__title">
                                1) Review the conversation topics
                              </h2>

                              <div className="question-categories-choice__styles">
                                <p className="queestion-categories-choice__styles--microcopy">
                                  When considering the topics to take to your manager meeting, please
                                  take time to explore them and reflect upon whether and how they
                                  cause you stress.
                                  We have a useful guide on how to complete your team member prep in
                                  our resources area.{' '}
                                  <Link
                                    to={
                                      process.env.PUBLIC_URL + '/Team Member Preparation Guidance.pdf'
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    View team member prep guide.
                                  </Link>
                                </p>

                                <p>

                                </p>
                              </div>
                            </>
                          )}

                          {!submissionUnlocked && (
                            <h2 className="question-categories-choice__title">
                              Your conversation topics
                            </h2>
                          )}

                          <Grid className="grid-padding-x">
                            {selectedCategories?.map((questionCategory) => (
                              <Cell
                                small={12}
                                large={4}
                                key={`selected-category-${questionCategory.id}`}
                                style={{ display: 'flex', flexDirection: 'column' }}
                              >
                                <QuestionCategory
                                  categoryFormButtons={categoryFormButtons}
                                  closeModal={closeModal}
                                  currentModal={currentModal}
                                  deselectCategory={deselectCategory}
                                  handleChange={handleChange}
                                  openModal={openModal}
                                  questionCategory={questionCategory}
                                  questionResultsContent={questionResultsContent}
                                  selectCategory={null}
                                  selectedCategories={selectedCategories}
                                  submissionUnlocked={submissionUnlocked}
                                  showSummary={true}
                                  showNotes={false} // Hide the internal textarea in the modal/card
                                />
                                {submissionUnlocked && (
                                  <div className="category-notes-outer">
                                    <textarea
                                      className="category-notes"
                                      placeholder="Topic Notes..."
                                      value={inputs[`notes--${questionCategory.id}`] || ''}
                                      data-complete="true"
                                      onChange={handleChange}
                                      name={`notes--${questionCategory.id}`}
                                    />
                                  </div>
                                )}
                              </Cell>
                            ))}

                            {submissionUnlocked &&
                              selectedCategories.length < 3 &&
                              Array.from({ length: 3 - selectedCategories.length }).map((_, i) => (
                                <Cell small={12} large={4} key={'pending-category-' + i}>
                                  <div className="category-form-field-wrap">
                                    <div className="category-form-field category-form-field--empty">
                                      <HashLink
                                        to="#deselected-categories"
                                        className="category-form-field__button category-form-field__button--secondary"
                                        id="page-function-button--add"
                                        onClick={openModal}

                                        data-modal-id="category-private-notes"
                                      >
                                        Add topic
                                        <PlusCircle
                                          size={21}
                                          className="category-form-field__button-icon category-form-field__button-icon--empty util--hide-icon"
                                        />
                                      </HashLink>
                                    </div>
                                  </div>

                                </Cell>))}

                          </Grid>
                        </div>

                        {!submissionUnlocked && (
                          <div className="page-content">
                            <h3 className="page-content__title">Your notes</h3>

                            {selectedCategories?.map(
                              (questionCategory) =>
                                questionResultsContent(questionCategory.id, 'notes') && (
                                  <>
                                    <h3 className="page-content__subtitle">
                                      Notes for{' '}
                                      {questionResultsContent(
                                        questionCategory.id,
                                        'optionalCategoryName',
                                      )
                                        ? questionResultsContent(
                                          questionCategory.id,
                                          'optionalCategoryName',
                                        )
                                        : questionCategory.name}
                                    </h3>
                                    {questionResultsContent(questionCategory.id, 'notes')}

                                  </>
                                ),
                            )}
                          </div>
                        )}

                        {submissionUnlocked && (
                          <>
                            <div className="question-categories-choice">
                              <h2 className="question-categories-choice__title">2) General Notes</h2>
                              <div className="question-categories-choice__styles question-categories-choice__styles--microcopy">
                                <p className="queestion-categories-choice__styles--microcopy">
                                  Take time to understand the topics and review the examples then make any notes that you feel will help you to ensure that you include everything you would like to communicate in the meaning.
                                </p>
                              </div>
                              <div className="category-notes-outer">
                                <textarea
                                  id="general_notes"
                                  name="general_notes"
                                  placeholder="General Notes..."
                                  className="category-notes"
                                  data-complete="true"
                                  value={inputs.general_notes || ''}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>

                            <div className="question-categories-choice">
                              <h2 className="question-categories-choice__title">
                                3) Submit your prep
                              </h2>

                              <Grid className="grid-padding-x">
                                <Cell small={12} large={9}>
                                  <div className="question-categories-choice__styles question-categories-choice__styles--microcopy">
                                    <p className="queestion-categories-choice__styles--microcopy">When you are happy with the topics you have chosen</p>
                                  </div>
                                </Cell>
                                <Cell small={12} large={3} className="text-right">
                                  {formChanged && (
                                    <>
                                      {selectedCategories.length >= 1 && (
                                        <div className="basic-form__actions basic-form__actions--inline">
                                          <input
                                            value="Next Step"
                                            data-complete="true"
                                            type="submit"
                                            className="basic-form__button basic-form__button--inline"
                                            onClick={handleSubmit}
                                            ref={(ref) => (basicFormButtons.current[1] = ref)}
                                            id="page-form-button--complete"
                                          />
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {!formChanged && selectedCategories.length >= 1 && (
                                    <Grid className="grid-padding-x">
                                      <Cell small={12} className="text-right">
                                        <Link
                                          to={'/conversations/' + data.id + '/confirmations/new'}
                                          className="page-content__button--medium"
                                          id="page-form-button--complete"
                                        >
                                          Next Step
                                        </Link>
                                      </Cell>
                                    </Grid>
                                  )}
                                </Cell>
                              </Grid>
                            </div>

                            {currentModal === 'category-private-notes' && (
                              <div
                                className="modal fade show"
                                tabIndex="-1"
                                style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                              >
                                <Modal show={currentModal === 'category-private-notes'} dialogClassName='new-modal-dialog' className='topic-modal'>
                                  <Modal.Header className='justify-content-center'>
                                    <div className="modal-header border-0" style={{ position: 'relative' }}>
                                      <h5 className="modal-title w-100 text-center">
                                        Select Your Conversation Topics (Max 3)
                                      </h5>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn-close-categories"
                                      aria-label="Close"
                                      onClick={closeModal}
                                      style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 2 }}
                                    >
                                      <span aria-hidden="true">&times;</span>
                                    </button>
                                  </Modal.Header>

                                  <Modal.Body>
                                    <div className="category-form-modal">
                                      {submissionUnlocked && (
                                        <div className="grid-container">
                                          <Grid className="grid-padding-x">
                                            <Cell small={12}>
                                              {(selectedCategories || deselectedCategories) && (
                                                <Grid className="grid-padding-x">
                                                  {[...selectedCategories, ...deselectedCategories].map((questionCategory) => (
                                                    <Cell
                                                      small={12}
                                                      large={4}
                                                      key={'modal-question-' + questionCategory.id}
                                                    >
                                                      <QuestionCategory
                                                        categoryFormButtons={categoryFormButtons}
                                                        closeModal={closeModal}
                                                        currentModal={currentModal}
                                                        deselectCategory={null}
                                                        handleChange={handleChange}
                                                        openModal={openModal}
                                                        questionCategory={questionCategory}
                                                        questionResultsContent={questionResultsContent}
                                                        selectCategory={selectCategory}
                                                        selectedCategories={selectedCategories}
                                                        submissionUnlocked={submissionUnlocked}
                                                        showSummary={true}
                                                        showNotes={true}
                                                      />
                                                    </Cell>
                                                  ))}
                                                </Grid>
                                              )}
                                            </Cell>
                                          </Grid>
                                        </div>
                                      )}
                                    </div>
                                  </Modal.Body>
                                  <Modal.Footer className='justify-content-center'>
                                    <div className="modal-footer border-0 justify-content-center">
                                      <button
                                        className="page-content__button--medium mb-3"
                                        id="page-navigation-button--save-changes"
                                        onClick={selectCategories}
                                      >
                                        Save Chosen Topics
                                      </button>
                                    </div>
                                  </Modal.Footer>
                                </Modal>
                              </div>
                            )}
                          </>
                        )}

                        {!submissionUnlocked && inputs.general_notes && (
                          <>
                            <h2 className="question-categories-choice__title">Your private notes</h2>

                            <div className="question-categories-choice__styles">
                              {inputs.general_notes}
                            </div>
                          </>
                        )}
                      </form>
                    )}
                  </>
                )}
              </Cell>
            </Grid>
          </div>
        ) : (
          <div className="grid-container" style={{ minHeight: '70vh' }}>
            <Grid className="grid-padding-x">
              <Cell small={12}>
                <div className="page-content" style={{ padding: '2rem', textAlign: 'center' }}>
                  {loading && (
                    <div>
                      <h2>Loading conversation data...</h2>
                      <p>Please wait while we load your conversation details.</p>
                    </div>
                  )}
                  {error && !loading && (
                    <div>
                      <h2>Unable to load conversation</h2>
                      <p>There was an issue loading the conversation data. Please try refreshing the page.</p>
                      <button
                        className="page-content__button--medium"
                        onClick={() => window.location.reload()}
                      >
                        Refresh Page
                      </button>
                    </div>
                  )}
                  {!loading && !error && (
                    <div>
                      <h2>Setting up your conversation...</h2>
                      <p>We're preparing your conversation categories. This should only take a moment.</p>
                    </div>
                  )}
                </div>
              </Cell>
            </Grid>
          </div>
        )}
      </div>
    </div>
  );
}
